import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { CheckCircle2, AlertCircle, Clock, MinusCircle, Gauge, RefreshCw, Rocket, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

/**
 * Conveyancing Lodgement Readiness panel (Phase Conv-1b.2).
 *
 * Aggregates TYPE 1 (system-generated) + TYPE 2 (tracked external) documents
 * into a single readiness % + checklist with red/amber/green status +
 * "Ready to Lodge: YES/NO" headline.
 *
 * Renders only for matter_type ∈ {conveyancing, otp}.
 */
const STATUS_META = {
  ok:           { icon: CheckCircle2, color: "text-emerald-600",  bg: "bg-emerald-50  border-emerald-200" },
  pending:      { icon: AlertCircle,  color: "text-red-600",      bg: "bg-red-50      border-red-200" },
  in_progress:  { icon: Clock,        color: "text-amber-600",    bg: "bg-amber-50    border-amber-200" },
  "n/a":        { icon: MinusCircle,  color: "text-zinc-400",     bg: "bg-zinc-50     border-zinc-200" },
};

const OVERALL_META = {
  green: { label: "Ready to Lodge", chip: "bg-emerald-500 text-white" },
  amber: { label: "Almost Ready",   chip: "bg-amber-500 text-zinc-900" },
  red:   { label: "Not Ready",      chip: "bg-red-600 text-white" },
};

export default function LodgementReadinessPanel({ matter, refreshSignal, onChanged }) {
  const mtype = matter?.matter_type || matter?.service_type;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lodging, setLodging] = useState(false);

  const load = useCallback(() => {
    if (!matter?.id) return;
    setLoading(true);
    api.get(`/admin/matters/${matter.id}/lodgement-readiness`)
      .then((res) => setData(res.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [matter?.id]);

  useEffect(() => { load(); }, [load, refreshSignal]);

  if (mtype !== "conveyancing" && mtype !== "otp") return null;
  if (!data) {
    return (
      <div className="bg-white border border-zinc-200 p-5 mb-5" data-testid="lodgement-readiness-panel">
        <div className="text-xs text-zinc-500">Loading lodgement readiness…</div>
      </div>
    );
  }

  const overall = OVERALL_META[data.overall_status] || OVERALL_META.red;

  return (
    <div className="bg-white border border-zinc-200 mb-5" data-testid="lodgement-readiness-panel">
      {/* Header */}
      <div className="px-5 py-4 bg-zinc-900 text-white flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Gauge className="h-5 w-5" />
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-300">Lodgement Readiness</div>
            <div className="text-sm font-medium">Aggregate progress · Type 1 + Type 2 documents</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className={`text-[11px] uppercase tracking-wider px-3 py-1 font-medium ${overall.chip}`}
                data-testid="lodgement-overall-status">
            {overall.label}
          </span>
          <span className="text-2xl font-light tabular-nums" data-testid="lodgement-pct">
            {data.pct}%
          </span>
          <button
            type="button"
            onClick={load}
            className="text-zinc-400 hover:text-white p-1"
            title="Refresh"
            data-testid="lodgement-refresh"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {/* Ready-to-lodge banner */}
      <div className={`px-5 py-3 border-b text-sm flex items-center gap-2 flex-wrap
                       ${data.ready_to_lodge
                          ? "bg-emerald-50 border-emerald-200 text-emerald-900"
                          : "bg-red-50 border-red-200 text-red-900"}`}
           data-testid="ready-to-lodge-banner">
        {data.ready_to_lodge
          ? <CheckCircle2 className="h-4 w-4 text-emerald-600" />
          : <AlertCircle  className="h-4 w-4 text-red-600" />}
        <span className="font-medium">
          Ready to Lodge: {data.ready_to_lodge ? "YES" : "NO"}
        </span>
        <span className="text-xs opacity-75">
          ({data.blocking_ok} of {data.blocking_total} critical items complete)
        </span>

        {/* Lodge Now CTA — only when ready and not yet lodged */}
        {data.ready_to_lodge && matter?.status !== "lodged" && (
          <Button
            size="sm"
            disabled={lodging}
            className="ml-auto rounded-none bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="lodge-now-btn"
            onClick={async () => {
              if (!window.confirm("Lodge this matter? This will mark all signed documents as ready for lodgement, fire the lodgement-milestone invoice, and set the matter status to 'lodged'. This action is irreversible.")) return;
              setLodging(true);
              try {
                const res = await api.post(`/admin/matters/${matter.id}/lodge`, {});
                toast.success(`Matter lodged — ${res.data?.ready_for_lodgement_count} documents marked ready`);
                load();
                onChanged?.();
              } catch (e) {
                toast.error(formatApiError(e));
              } finally {
                setLodging(false);
              }
            }}
          >
            {lodging
              ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Lodging…</>
              : <><Rocket className="h-3.5 w-3.5 mr-1.5" /> Lodge Now</>}
          </Button>
        )}

        {matter?.status === "lodged" && (
          <span className="ml-auto text-[11px] uppercase tracking-wider bg-emerald-600 text-white px-3 py-1 font-medium" data-testid="lodged-badge">
            LODGED
          </span>
        )}
      </div>

      {/* Sections */}
      <div className="divide-y divide-zinc-200">
        <Section title="Lodgement Core · Transfer instruments" testid="lodgement-section-core"
                 items={data.sections.lodgement_core} />
        <Section title="SARS &amp; FICA Legal Completion"      testid="lodgement-section-legal"
                 items={data.sections.legal_completion} />
        <Section title="Contract (Offer to Purchase)"          testid="lodgement-section-contract"
                 items={data.sections.contract} />
        <Section title="External Documents · Type 2"           testid="lodgement-section-external"
                 items={data.sections.external} />
      </div>

      {data.blocking_pending && data.blocking_pending.length > 0 && (
        <div className="px-5 py-3 bg-red-50 border-t border-red-200" data-testid="lodgement-blocking-list">
          <div className="text-xs font-medium text-red-900 flex items-center gap-1.5 mb-1.5">
            <AlertCircle className="h-3.5 w-3.5" />
            Critical items still required to lodge
          </div>
          <ul className="text-[12px] text-red-800 list-disc pl-5 space-y-0.5">
            {data.blocking_pending.map((b) => (
              <li key={`${b.type}-${b.key}`} data-testid={`lodgement-missing-${b.key}`}>
                <span className="font-medium">{b.label}</span>
                <span className="text-red-600 ml-1">· {b.status === "in_progress" ? "in progress" : "missing"}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}


function Section({ title, items, testid }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="px-5 py-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mb-2">{title}</div>
      <ul className="space-y-1">
        {items.map((it) => {
          const meta = STATUS_META[it.status] || STATUS_META.pending;
          const Icon = meta.icon;
          return (
            <li key={it.key}
                className={`flex items-center gap-2.5 text-[13px] border px-2.5 py-1.5 ${meta.bg}`}
                data-testid={`lodgement-item-${it.key}`}>
              <Icon className={`h-4 w-4 ${meta.color} shrink-0`} />
              <span className={`flex-1 ${it.status === "n/a" ? "text-zinc-400 line-through" : "text-zinc-800"}`}>
                {it.label}
              </span>
              {it.blocking && it.status !== "ok" && (
                <span className="text-[10px] uppercase tracking-wider text-red-700 font-medium">Critical</span>
              )}
              <span className="text-[11px] text-zinc-500">{it.hint}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
